/**
 * ============================================
 * DASHBOARD HEADER
 * ============================================
 *
 * Layout (desktop):
 *   LEFT:   Logo · App Name · Version · Mode Toggle · Status
 *   RIGHT:  [Alert Bell]  |  [Download · Remote Config · Settings]  |  [Theme · User · Logout]
 *
 * Items on the right are grouped into visual clusters
 * separated by subtle dividers for clarity.
 */

import { Button } from '@/components/ui/Button';
import { LogOut, Settings, Globe, ShieldCheck, Eye, Activity } from 'lucide-react';
import { appMetadata } from '@/config';
import { AlertBell } from '@/components/alerts';
import { DownloadDropdown } from './DownloadDropdown';
import { getEffectiveRole } from '@/utils/roles';

interface DashboardHeaderProps {
  username: string;
  userGroups?: string[];
  isExternal?: boolean;
  isMonitor?: boolean;
  /**
   * True when the page is running inside the no-login "Lokko Monitor"
   * Android APK. Hides the logout button -- this variant is permanently
   * signed in by design and there is no Cognito session to terminate.
   */
  isMonitorApp?: boolean;
  onLogout: () => void;
  appName: string;
  onShowAlertHistory: () => void;
  onShowSettings: () => void;
  onShowRemoteConfig: () => void;
  onExportCSV: () => void;
  modeToggle?: React.ReactNode;
  themeToggle?: React.ReactNode;
  statusOverview?: React.ReactNode;
}

function Divider() {
  return <div className="h-6 w-px bg-border/60" />;
}

export function DashboardHeader({ username, userGroups, isExternal = false, isMonitor = false, isMonitorApp = false, onLogout, appName, onShowAlertHistory, onShowSettings, onShowRemoteConfig, onExportCSV, modeToggle, themeToggle, statusOverview }: DashboardHeaderProps) {
  // Role label shows the effective role (admin > external > monitor), not
  // the first arbitrary group. Falls back to the raw first group if the
  // user has groups but none of them are recognised -- the Dashboard's
  // no-access gate normally prevents that case from reaching this render.
  const effectiveRole = getEffectiveRole(userGroups);
  const roleLabel = effectiveRole ?? (userGroups && userGroups.length > 0 ? userGroups[0] : null);
  const isAdmin = effectiveRole === 'admin';
  // External: silent viewer -- no alerts, no downloads, no settings.
  // Monitor:  needs Downloads (for native alarms on Android/iOS/Win) +
  //           AlertBell, but NO Settings / Remote Config / CSV export.
  // Default:  everything.
  const showAlerts = !isExternal;
  const showDownloads = !isExternal;                // includes monitor
  const showSettingsTools = !isExternal && !isMonitor;  // Settings + Remote Config

  return (
    <header className="sticky top-0 z-50 w-full border-b bg-background/95 backdrop-blur">
      <div className="container mx-auto px-2 sm:px-4 py-2">

        {/* ───── Desktop layout ───── */}
        <div className="hidden sm:flex items-center justify-between gap-2">
          {/* Left: Brand + Navigation */}
          <div className="flex items-center gap-3 min-w-0 flex-shrink">
            <div className="h-8 w-8 rounded-lg overflow-hidden flex-shrink-0">
              <img src="icons/lokko.png" alt="" className="h-full w-full object-cover" />
            </div>
            <div className="flex items-baseline gap-2 flex-shrink-0">
              <h1 className="font-semibold">{appName}</h1>
              <span className="text-xs text-muted-foreground">v{appMetadata.version}</span>
            </div>
            {modeToggle && <div className="ml-4 flex-shrink-0">{modeToggle}</div>}
            {statusOverview && <div className="ml-4 flex-shrink-0 hidden lg:block">{statusOverview}</div>}
          </div>

          {/* Right: Grouped actions */}
          <div className="flex items-center gap-2.5 flex-shrink-0">

            {/* 1) Alerts – hidden for external users (monitor sees it) */}
            {showAlerts && (
              <>
                <AlertBell onShowHistory={onShowAlertHistory} />
                <Divider />
              </>
            )}

            {/* 2) Tools group -- per-button gated. Monitor gets only the
                 Download dropdown (with the CSV/reports section hidden) so
                 they can install the Android/iOS/Win clients for native
                 alarms. External gets nothing here. */}
            {(showDownloads || showSettingsTools) && (
              <>
                <div className="flex items-center gap-0.5 bg-muted/40 rounded-xl px-1 py-0.5">
                  {showDownloads && (
                    <DownloadDropdown onExportCSV={onExportCSV} hideReports={isMonitor} />
                  )}
                  {showSettingsTools && (
                    <>
                      <button
                        onClick={onShowRemoteConfig}
                        className="p-2 rounded-lg hover:bg-background/80 text-muted-foreground hover:text-foreground transition-colors"
                        title="Remote Config"
                      >
                        <Globe className="h-4.5 w-4.5" />
                      </button>
                      <button
                        onClick={onShowSettings}
                        className="p-2 rounded-lg hover:bg-background/80 text-muted-foreground hover:text-foreground transition-colors"
                        title="Settings"
                      >
                        <Settings className="h-4.5 w-4.5" />
                      </button>
                    </>
                  )}
                </div>
                <Divider />
              </>
            )}

            {/* 3) User group */}
            <div className="flex items-center gap-2.5">
              {themeToggle}
              <div className="flex items-center gap-2 bg-muted/40 rounded-xl pl-2.5 pr-1 py-1">
                {/* Name + role stacked */}
                <div className="flex flex-col items-end leading-none">
                  <span className="text-[13px] text-foreground font-medium">{username}</span>
                  {roleLabel && (
                    <div className={`flex items-center gap-1 mt-0.5 ${isAdmin ? 'text-emerald-500' : isMonitor ? 'text-blue-400' : 'text-muted-foreground'}`}>
                      {isAdmin
                        ? <ShieldCheck className="h-3 w-3" />
                        : isMonitor
                          ? <Activity className="h-3 w-3" />
                          : <Eye className="h-3 w-3" />
                      }
                      <span className="text-[9px] font-semibold uppercase tracking-widest">{roleLabel}</span>
                    </div>
                  )}
                </div>
                {/* Monitor APK is permanently signed in -- no logout. */}
                {!isMonitorApp && (
                  <Button variant="ghost" size="sm" onClick={onLogout} className="h-7 px-2 ml-0.5 text-muted-foreground hover:text-red-500 hover:bg-red-500/10">
                    <LogOut className="h-3.5 w-3.5" />
                  </Button>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* ───── Status row for small/medium screens (below lg) ───── */}
        {statusOverview && (
          <div className="hidden sm:flex lg:hidden items-center justify-center pt-2">
            {statusOverview}
          </div>
        )}

        {/* ───── Mobile layout ───── */}
        <div className="flex sm:hidden flex-col gap-2">
          {/* Row 1: Brand (left) | Actions (right) */}
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2 min-w-0">
              <div className="h-7 w-7 rounded-lg overflow-hidden flex-shrink-0">
                <img src="icons/lokko.png" alt="" className="h-full w-full object-cover" />
              </div>
              <span className="text-xs text-muted-foreground flex-shrink-0">v{appMetadata.version}</span>
            </div>

            <div className="flex items-center gap-1 flex-shrink-0">
              {/* Alerts – monitor sees, external does not */}
              {showAlerts && <AlertBell onShowHistory={onShowAlertHistory} />}

              {/* Tools -- per-button gated; monitor gets just Downloads */}
              {(showDownloads || showSettingsTools) && (
                <div className="flex items-center gap-0 bg-muted/40 rounded-lg px-0.5 py-0.5 ml-1">
                  {showDownloads && (
                    <DownloadDropdown onExportCSV={onExportCSV} hideReports={isMonitor} />
                  )}
                  {showSettingsTools && (
                    <>
                      <button
                        onClick={onShowRemoteConfig}
                        className="p-1.5 rounded-md hover:bg-background/80 text-muted-foreground hover:text-foreground transition-colors"
                        title="Remote Config"
                      >
                        <Globe className="h-4 w-4" />
                      </button>
                      <button
                        onClick={onShowSettings}
                        className="p-1.5 rounded-md hover:bg-background/80 text-muted-foreground hover:text-foreground transition-colors"
                        title="Settings"
                      >
                        <Settings className="h-4 w-4" />
                      </button>
                    </>
                  )}
                </div>
              )}

              {/* User */}
              <div className="flex items-center gap-1 ml-1">
                {themeToggle}
                {/* Monitor APK is permanently signed in -- no logout. */}
                {!isMonitorApp && (
                  <Button variant="outline" size="sm" onClick={onLogout} className="h-7 px-2">
                    <LogOut className="h-3.5 w-3.5" />
                  </Button>
                )}
              </div>
            </div>
          </div>

          {/* Row 2: Mode Toggle */}
          {modeToggle && (
            <div className="flex items-center justify-center">
              {modeToggle}
            </div>
          )}

          {/* Row 3: Status Overview */}
          {statusOverview && (
            <div className="flex items-center justify-center">
              {statusOverview}
            </div>
          )}
        </div>

      </div>
    </header>
  );
}
